import {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Appearance} from 'react-native';
import {useGetnet3DSPayment} from '../3ds/GetnetWebview3ds/hooks/useGetnet3DSPayment';
import {useCardProcessingPolling} from '../hooks/useCardProcessingPolling';
import {PaymentResponse} from '../3ds/GetnetWebview3ds/types';
import {STATUS_PENDING_CHALLENGE} from '../3ds/GetnetWebview3ds/constants';
import {CardGatewayCallbacks, ChargeCardFn} from './types';

type Options = CardGatewayCallbacks & {
  chargeCard: ChargeCardFn;
};

export function useGetnetGateway({chargeCard, onSuccess, onError}: Options) {
  const [isPaying, setIsPaying] = useState(false);
  const [challengeData, setChallengeData] = useState<PaymentResponse | null>(
    null,
  );
  const [showChallenge, setShowChallenge] = useState(false);

  const previousColorSchemeRef = useRef<'light' | 'dark' | null>(null);
  const didForceLightRef = useRef(false);

  const {runPostChallengeFlow, isPolling: is3dsPolling, clearPolling: clear3dsPolling} =
    useGetnet3DSPayment({onSuccess, onError});

  const {startPolling: startCardPolling, isPolling: isCardPolling, clearPolling: clearCardPolling} =
    useCardProcessingPolling({onSuccess, onError});

  const isPolling = is3dsPolling || isCardPolling;

  const clearPolling = useCallback(() => {
    clear3dsPolling();
    clearCardPolling();
  }, [clear3dsPolling, clearCardPolling]);

  useEffect(() => {
    if (showChallenge) {
      previousColorSchemeRef.current = Appearance.getColorScheme() ?? null;
      Appearance.setColorScheme('light');
      didForceLightRef.current = true;
    } else if (didForceLightRef.current) {
      Appearance.setColorScheme(previousColorSchemeRef.current ?? null);
      previousColorSchemeRef.current = null;
      didForceLightRef.current = false;
    }
  }, [showChallenge]);

  useEffect(() => {
    return () => {
      clearPolling();
      if (didForceLightRef.current) {
        Appearance.setColorScheme(previousColorSchemeRef.current ?? null);
        didForceLightRef.current = false;
      }
    };
  }, [clearPolling]);

  const pay = useCallback(
    async (cvc: string) => {
      setIsPaying(true);
      try {
        const data = await chargeCard({
          cvc,
          three_ds_provider: 'getnet',
          is3ds: true,
        });
        if (data?.processando) {
          startCardPolling({ compra_id: data.compra_id });
          return;
        }
        if (data.result.status === STATUS_PENDING_CHALLENGE) {
          setChallengeData(data);
          setIsPaying(false);
          await new Promise(resolve => setTimeout(resolve, 400));
          setShowChallenge(true);
        } else {
          onSuccess();
        }
      } catch (err: any) {
        const response = err?.response;
        if (response?.data?.error !== 'Error getnet_3ds.') {
          onError(response?.data?.msg);
        } else {
          onError();
        }
      } finally {
        setIsPaying(false);
      }
    },
    [chargeCard, onSuccess, onError, startCardPolling],
  );

  const onChallengeNavChange = useCallback(
    (navState: any) => {
      const url = navState?.url || '';
      if (
        /success|callback|complete|authenticated/i.test(url) ||
        url.includes('baratao') ||
        url.includes('getnet')
      ) {
        setShowChallenge(false);
        if (challengeData) {
          runPostChallengeFlow(challengeData.result.ds_trans_id);
        }
      } else if (/error|declined|failed|cancel/i.test(url)) {
        setShowChallenge(false);
        onError();
      }
    },
    [challengeData, runPostChallengeFlow, onError],
  );

  const onChallengeCancel = useCallback(() => {
    Alert.alert(
      'Cancelar autenticação',
      'Tem certeza que deseja cancelar a autenticação 3DS? O pagamento não será processado.',
      [
        {text: 'Manter', style: 'cancel'},
        {
          text: 'Cancelar autenticação',
          style: 'destructive',
          onPress: () => {
            clearPolling();
            setShowChallenge(false);
            setTimeout(() => onError(), 300);
          },
        },
      ],
      {cancelable: true},
    );
  }, [clearPolling, onError]);

  return {
    pay,
    isPaying,
    isPolling,
    challengeData,
    showChallenge,
    onChallengeNavChange,
    onChallengeCancel,
    clearPolling,
  };
}
